/// <reference types="vite-plugin-svgr/client" />

import { BellOutlined, LogoutOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { Avatar, Badge, Button } from 'antd';
import { useNavigate } from 'react-router-dom';

import ChevronLeftSvg from '../../assets/icons/chevron-left.svg?react';
import { Organization, UserOrganizationAccess } from '../../api/organization';
import {
  AccessTokenUser,
  OrganizationAccessRoles,
  logoutUser,
  setCurrentlyActiveOrganization,
} from '../../common/auth';
import './MenuSideBarBottom.scss';
import { useOrgUserManagement } from '../../pages/Settings/UserManagement/NewUserManager/NewUserManager';
import InvitationModal from '../InvitationModal/InvitationModal';
import colorTokens from '../../constants/design_system/colors';
import {
  useGetOrganization,
  useGetOrganizationAccessesAndInvitesWithOrganization,
} from '../../serverState/organization';
import { ActiveOrganizationItem } from './components/ActiveOrganizationItem';
import { OrganizationItem } from './components/OrganizationItem';
import RoutePaths from '../../router/RoutePaths';
import ListItem from '../UI/Components/ListItem';
import CompanyLogoSvg from '../../assets/icons/company-logo.svg?react';
import SideBarItem from '../UI/Components/SideBarItem';
import SpaceBarItem from '../UI/Components/SpaceBarItem';
import { useState } from 'react';
import { AllTasksIcon } from '../../assets/CustomIcons/AllTasksIcon';
import { CompassIcon } from '../../assets/CustomIcons/CompassIcon';
import { getUserProfilePictureUrl } from '../../common/userHelper';
import BaseDropdown from '../UI/Dropdown/BaseDropdown';
import ThemeSwitcher from '../ThemeSwitcher/ThemeSwitcher';
import FeedbackButton from '../Feedback/FeedbackButton';

type Props = {
  activeOrganization?: Organization;
  collapsed: boolean;
  notificationsCount?: number;
  user?: AccessTokenUser;
  organizationRole: OrganizationAccessRoles;
  inboxOpen: boolean;
  onToggleInbox: () => void;
  inboxCount?: number;
};

const MenuItemProfile = ({
  activeOrganization,
  collapsed,
  notificationsCount = 0,
  user,
  organizationRole,
  inboxOpen,
  onToggleInbox,
  inboxCount,
}: Props) => {
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);

  const organizationQuery = useGetOrganization(activeOrganization?.id as number);
  const allUserOrgAcessesQuery = useGetOrganizationAccessesAndInvitesWithOrganization();
  const navigate = useNavigate();
  const userOrgAcesses: UserOrganizationAccess[] =
    allUserOrgAcessesQuery.data?.organizationsAccess?.filter((org) => org.deletedAt === null) || [];
  const activeOrgAccess = userOrgAcesses.find((org) => org.id === activeOrganization?.id);
  const isAdmin = organizationRole && organizationRole === OrganizationAccessRoles.ADMIN;

  const {
    inviteModalOpen,
    setInviteModalOpen,
    query,
    currentUserOrgAccess,
    contextHolder,
    handleMenuClick,
    handleInviteSubmit,
    getItems,
    getUserRoleComponent,
  } = useOrgUserManagement();

  const selectOrganization = async (orgAccess: UserOrganizationAccess) => {
    await setCurrentlyActiveOrganization(orgAccess);

    navigate(RoutePaths.Home);
    window.location.reload();
  };

  return (
    <ul className="menu_bottom_list">
      {contextHolder}
      {inviteModalOpen && (
        <InvitationModal
          open={inviteModalOpen}
          onOk={handleInviteSubmit}
          onCancel={() => {
            setInviteModalOpen(false);
          }}
        />
      )}

      <div className={`flex items-center mb-3 ${collapsed ? 'flex-col gap-2' : 'gap-3'}`}>
        <ThemeSwitcher collapsed={collapsed} />
        <FeedbackButton collapsed={collapsed} />
      </div>

      <div className={`flex flex-col gap-1 rounded-lg bg-brand-white mb-2 ${collapsed ? 'py-3 px-1.5' : 'p-3'}`}>
        {import.meta.env.VITE_FEATURE_ALL_TASKS === 'true' && (
          <SpaceBarItem
            iconLeft={<AllTasksIcon style={{ fontSize: 16 }} />}
            text="All Tasks"
            selected={window.location.pathname.includes('/all-tasks')}
            collapsed={collapsed}
            onClick={() => navigate(`/${activeOrganization?.publicId}/all-tasks`)}
            textClassName="font-medium"
          />
        )}

        {import.meta.env.VITE_FEATURE_INBOX === 'true' && (
          <SpaceBarItem
            iconLeft={<CompassIcon style={{ fontSize: 16 }} />}
            iconRight={inboxCount ? <Badge color={colorTokens.orange[600]} count={inboxCount} /> : undefined}
            text="Decisions"
            selected={window.location.pathname.includes('/decisions')}
            collapsed={collapsed}
            onClick={() => navigate(`/${activeOrganization?.publicId}/decisions`)}
            textClassName="font-medium"
          />
        )}

        <SpaceBarItem
          iconLeft={<BellOutlined style={{ fontSize: 16 }} />}
          iconRight={notificationsCount > 0 ? <Badge color={colorTokens.orange[600]} count={notificationsCount} /> : undefined}
          text="Inbox"
          selected={inboxOpen}
          collapsed={collapsed}
          onClick={(e) => {
            e.stopPropagation();
            onToggleInbox();
          }}
          textClassName="font-medium"
        />
      </div>

      {user ? (
        <BaseDropdown
          trigger={['click']}
          onOpenChange={(open) => {
            setIsOrgDropdownOpen(open);
          }}
          dropdownRender={(menu) => (
            <div className="org-dropdown absolute left-full bottom-0 ml-1 w-[244px] flex flex-col bg-brand-white rounded-lg shadow-md max-h-[80vh] p-4">
              <div className="flex items-center justify-between gap-2 mb-2 flex-shrink-0">
                <div className="text-neutral-900 text-xs font-semibold leading-none">{user.user.email}</div>
                <BaseDropdown
                  trigger={['click']}
                  placement="topLeft"
                  menu={{
                    items: [
                      {
                        key: '1',
                        label: 'Organizations',
                        icon: <CompanyLogoSvg />,
                      },
                    ],
                    onClick: (e) => {
                      navigate(RoutePaths.LoginSelectOrganization);
                    },
                  }}
                >
                  <Button icon={<MoreOutlined />} size="small" />
                </BaseDropdown>
              </div>

              <div className="overflow-y-auto flex flex-col gap-2 min-h-0">
                {activeOrgAccess && <ActiveOrganizationItem organizationAccess={activeOrgAccess} isAdmin={isAdmin} />}
                {userOrgAcesses
                  .filter((org) => org.id !== activeOrganization?.id)
                  .sort((orgA, orgB) => orgA.name.localeCompare(orgB.name))
                  .map((org) => (
                    <OrganizationItem key={org.id} organizationAccess={org} onClick={selectOrganization} />
                  ))}
              </div>

              <div className="flex flex-col gap-2 flex-shrink-0 mt-2">
                <ListItem
                  icon={<PlusOutlined />}
                  text={'Create New Organization'}
                  selected={false}
                  onClick={() => navigate(RoutePaths.LoginSelectOrganization)}
                  className="pl-2"
                />

                <div className="h-px bg-neutral-300 rounded-sm" />

                <ListItem
                  icon={<LogoutOutlined />}
                  text={'Logout'}
                  selected={false}
                  onClick={logoutUser}
                  className="pl-2"
                />
              </div>
            </div>
          )}
        >
          <SideBarItem
            iconLeft={
              <Avatar
                className={`flex-shrink-0 text-neutral-900 transition-colors duration-300 ${collapsed ? 'bg-white' : 'bg-brand-grey-light'}`}
                src={getUserProfilePictureUrl(organizationQuery.data?.organizationPicturePath)}
                shape="square"
                size={24}
              >
                {activeOrganization?.name.substring(0, 1)}
              </Avatar>
            }
            iconRight={<ChevronLeftSvg className="w-5 h-5 rotate-180" />}
            text={activeOrganization?.name || ''}
            selected={isOrgDropdownOpen}
            collapsed={collapsed}
          />
        </BaseDropdown>
      ) : (
        <div className="mx-2 p-3 cursor-pointer hover:bg-[#0000000f] rounded-lg" onClick={logoutUser}>
          <LogoutOutlined />
          Logout
        </div>
      )}
    </ul>
  );
};

export default MenuItemProfile;
